/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, FlexContainer, SVGIcon, theme } from "@nordcloud/gnui";
import { APP_ENV, CODE_VERSION } from "~/config";
import { useCustomers } from "~/services/customers";
import { logger } from "~/services/datadog";
import { useYbug } from "~/services/ybug";
import { AvatarIcon } from "../HeaderIcons";
import { DropdownWrap } from "./DropdownWrap";
import { FeedbackButton, UserImage } from "./styles";

export function User() {
  const { user } = useAuth0();
  const { selectedCustomer } = useCustomers();
  const { ybug } = useYbug();

  const customer = selectedCustomer?.name ?? "";
  const userName = user?.name ?? "";

  useYbugInit({ tenant: customer ?? "Unknown", userEmail: user?.email ?? "" });

  const handleFeedbackClick = () => {
    ybug?.open("feedback");
  };

  const showFeedback = ybug != null;

  return (
    <>
      {showFeedback && <Feedback onClick={handleFeedbackClick} />}
      <DropdownWrap>
        <FlexContainer
          data-testid="user-menu"
          columnGap={theme.spacing.spacing02}
        >
          {user?.picture ? (
            <UserImage src={user?.picture} alt={userName} />
          ) : (
            <AvatarIcon />
          )}
        </FlexContainer>
      </DropdownWrap>
    </>
  );
}

function useYbugInit({
  tenant,
  userEmail,
}: {
  tenant: string;
  userEmail: string;
}) {
  const { ybug, setYbugSettings, loadingStatus } = useYbug();

  useEffect(() => {
    if (loadingStatus === "error") {
      logger?.warn("There was an error while loading Ybug.");
    }

    if (loadingStatus === "ready" && ybug == null) {
      logger?.warn("Ybug client is not available after it was loaded.");
    }
  }, [loadingStatus, ybug]);

  useEffect(() => {
    setYbugSettings({
      feedback: { email: userEmail },
      user: {
        id: userEmail,
        email: userEmail,
        tenant: tenant || "Unknown",
        currentView: window.location.href,
        environment: APP_ENV,
        codeVersion: CODE_VERSION,
      },
    });
  }, [tenant, setYbugSettings, userEmail]);
}

type FeedbackProps = {
  onClick: () => void;
};

function Feedback({ onClick }: FeedbackProps) {
  return (
    <FeedbackButton onClick={onClick}>
      <Box>
        <SVGIcon
          name="feedback"
          css={{
            display: "block",
            width: theme.spacing.spacing06,
            height: theme.spacing.spacing06,
          }}
        />
      </Box>
    </FeedbackButton>
  );
}
